import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	BFFConsumerEnum,
	BFFProvider,
	useBFFDataProvider,
} from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsMenuProps,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../types';
import { GlobalShortcutsWithIntlProvider } from '../utils/locale';
import { UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME } from '../utils/ufo/constants';
import { useGlobalShortcutsData } from '../utils/use-global-shortcuts-data';

import { DragAndDropOptionsProvider } from './drag-and-drop-options-provider';
import { ShortcutMenuItem } from './shortcut-menu-item';
import { ViewAllAppsItem } from './view-all-apps-item';

export function GlobalShortcuts(props: GlobalShortcutsProps) {
	return (
		<UFOSegment name={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}>
			<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SHORTCUTS}>
				<GlobalShortcutsInner {...props} />
			</BFFProvider>
		</UFOSegment>
	);
}

function GlobalShortcutsInner(props: GlobalShortcutsProps) {
	const {
		cloudId,
		product,
		shortcutItems: rawShortcutItems,
		onUpdateGetFilteredProductLinks,
		testId,
		enableDragAndDrop,
		viewAllAppsConfig,
		openInSameTab,
	} = props;
	const onUpdateGetFilteredProductLinksRef = useRef<any>(onUpdateGetFilteredProductLinks);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;
	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product },
		disableAnalytics: true,
	});

	const shortcutItems = useGlobalShortcutsData({
		shortcutsConfig: rawShortcutItems,
		cloudId,
		onError: handleError,
		product,
	});

	const { status, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		forceStatic: true,
		product,
	});

	const productLinksRef = useRef(productLinks);
	productLinksRef.current = productLinks;

	const getBffProductLinksForKey = useCallback<fnGetFilteredProductLinks>(
		(apsKey: string) => ({
			links: productLinksRef.current[apsKey] ?? [],
			status,
		}),
		[status],
	);

	useEffect(() => {
		if (onUpdateGetFilteredProductLinksRef.current) {
			onUpdateGetFilteredProductLinksRef.current(getBffProductLinksForKey);
		}
	}, [getBffProductLinksForKey]);

	return (
		<GlobalShortcutsMenu
			enableDragAndDrop={enableDragAndDrop}
			openInSameTab={openInSameTab}
			product={product}
			shortcutItems={shortcutItems}
			testId={testId}
			viewAllAppsConfig={viewAllAppsConfig}
		/>
	);
}

export function GlobalShortcutsMenu({
	testId,
	shortcutItems = [],
	product,
	enableDragAndDrop,
	viewAllAppsConfig = {},
	openInSameTab,
}: GlobalShortcutsMenuProps) {
	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems.map((item, index) => {
			let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
			if (item.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				key = item?.apsKey ? item.apsKey : `shortcut-${index}`;
			}

			return {
				...item,
				key,
			};
		});
	}, [shortcutItems]);

	const createLinkItem = useCallback(
		(shortcut: MappedGlobalShortcutsItem) => {
			if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						dragAndDropEnabled={enableDragAndDrop}
					/>
				);
			}

			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					openInSameTab={openInSameTab}
					product={product}
				/>
			);
		},
		[product, openInSameTab, enableDragAndDrop],
	);

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map(createLinkItem)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<GlobalShortcutsWithIntlProvider>
			<NavigationAnalyticsContext data={{ product }}>
				<Stack testId={testId}>
					{enableDragAndDrop ? (
						<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
							{shortcutList}
						</DragAndDropOptionsProvider>
					) : (
						shortcutList
					)}
				</Stack>
			</NavigationAnalyticsContext>
		</GlobalShortcutsWithIntlProvider>
	);
}
